import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { MoreHorizontal, Eye, Edit, AlertTriangle } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface Asset {
  assetName: string;
  score: number;
  status: string;
}

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "critical":
    case "high":
      return "bg-red-100 text-red-800";
    case "warning":
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "maintenance":
      return "bg-orange-100 text-orange-800";
    case "active":
    case "good":
      return "bg-green-100 text-green-800";
    case "poor":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const OverviewTab: React.FC<{ data: any }> = ({ data }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use data from props or fallback to empty arrays
  const poorConditionAssets = data?.assetsPoorCondition || [];
  const highestRiskAssets = data?.assetsHighestRisk || [];

  // Common columns for both tables
  const assetColumns: TableColumn<Asset>[] = useMemo(
    () => [
      {
        key: "assetName",
        header: "Asset Name",
        type: "text",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "score",
        header: "Score",
        type: "custom",
        render: (value: number) => (
          <span className="font-medium text-blue-600">{value}</span>
        ),
      },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: () => "secondary",
          getClassName: (value: string) => getStatusColor(value),
        },
        render: (value: string) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
    ],
    []
  );

  // Actions for both tables
  const assetActions: TableAction<Asset>[] = useMemo(
    () => [
      {
        label: "Schedule Inspection",
        onClick: (row: any) => {
          navigate(`/ax/service-order/landing/create`);
        },
        icon: "Calendar",
        variant: "outline",
      },
      {
        label: "Schedule Maintenance",
        onClick: (row: any) => {
          navigate(`/ax/service-order/landing/create`);
        },
        icon: "Wrench",
        variant: "outline",
      },
    ],
    [toast]
  );

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets in Poor Condition */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-5 h-5 bg-red-100 rounded flex items-center justify-center">
                <AlertTriangle className="h-3 w-3 text-red-600" />
              </div>
              <p className="page-title">Assets in Poor Condition</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-3">
            <GenericTable
              data={poorConditionAssets}
              columns={assetColumns}
              rowKey="assetName"
              actions={assetActions}
              emptyMessage="No assets found"
            />
          </CardContent>
        </Card>

        {/* Assets with Highest Risk */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
                <AlertTriangle className="h-3 w-3 text-orange-600" />
              </div>
              <p className="page-title">Assets with Highest Risk</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-3">
            <GenericTable
              data={highestRiskAssets}
              columns={assetColumns}
              rowKey="assetName"
              actions={assetActions}
              emptyMessage="No assets found"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
